import moment from 'moment-timezone';

import Config from 'common-src/config';
import { getRoleById } from 'common-src/models/Client';
import { PlatformStatus } from 'common-src/models/Patient';
import { generateAthenaUrl } from 'common-src/models/PatientIntegrationsAthena';
import { TaskReason, TaskType } from 'common-src/models/PatientTask/constants';

import {
  ASSESSMENT_TASK_TYPES,
  ASSESSMENT_TASK_VARIANTS,
  BLOCKED_STATUS,
  CARE_ORCHESTRATION_BASE_PATH,
  ESCALATED_STATUS,
  ESCALATED_TO_OPTIONS,
  NOTIFICATION_BROWSER_COPY_BY_CATEGORY,
  PATHWAY,
  POST_VISIT_STALL_DAYS,
  ROLE_ID_TO_ESCALATION_TEAM,
  TAB_KEYS,
  TASK_CALLBACK_STATUS,
  TASK_DISPLAY_STATUS,
  TASK_DISPLAY_STATUS_BY_STATUS,
  TASK_PRIORITY,
  TASK_QUEUE_PRIORITY_GROUPS,
  TASK_QUEUE_REASON_LABELS,
  TASK_QUEUE_SEARCH_PLACEHOLDERS,
  TASK_QUEUE_SORT_MODE,
  TASK_STATUS,
  TASK_STATUS_FILTER_KEYS,
  TASK_STEP_TYPE,
} from './constants';

export const getPatient = (id, patients = []) => patients.find((p) => p.id === id);
export const getTask = (id, tasks = []) => tasks.find((t) => t.id === id);
export const athenaUrl = (athenaPatientId, practiceIdOverride) => {
  const practiceId = practiceIdOverride ?? JSON.parse(Config.ATHENA_MINDSPAN_GROUP_PRACTICE)?.value;
  if (!practiceId || !athenaPatientId) return null;
  return generateAthenaUrl({ practiceId, athenaPatientId });
};

export const EASTERN_TIMEZONE = 'America/New_York';

export const getEasternDateString = (isoValue) => {
  if (!isoValue) return null;
  const zoned = moment.tz(isoValue, EASTERN_TIMEZONE);
  return zoned.isValid() ? zoned.format('YYYY-MM-DD') : null;
};

export const getEasternDateTimeLabel = (isoValue) => {
  if (!isoValue) return null;
  const zoned = moment.tz(isoValue, EASTERN_TIMEZONE);
  if (!zoned.isValid()) return null;
  const abbreviation = zoned.format('z');
  const zoneSuffix =
    abbreviation && !abbreviation.startsWith('+') && abbreviation !== 'UTC'
      ? ` ${abbreviation}`
      : ' ET';
  return `${zoned.format('YYYY-MM-DD · HH:mm')}${zoneSuffix}`;
};

export const daysUntil = (d, timezone) => {
  if (!d) return NaN;

  const dateOnlyMatch =
    typeof d === 'string' ? d.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T|\s)/) : null;

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const target = new Date(Number(year), Number(month) - 1, Number(day));

    let today;
    if (timezone) {
      const nowInTz = moment.tz(timezone);
      today = new Date(nowInTz.year(), nowInTz.month(), nowInTz.date());
    } else {
      const now = new Date();
      today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }

    return Math.round((target - today) / 86400000);
  }

  return Math.round((new Date(d) - new Date()) / 86400000);
};

export const formatDue = (d) => {
  const n = daysUntil(d);
  if (n < 0) return { text: `${Math.abs(n)}d overdue`, urgent: true };
  if (n === 0) return { text: 'Today', urgent: true };
  if (n === 1) return { text: 'Tomorrow', urgent: false };
  return { text: `in ${n}d`, urgent: false };
};

export const overallReadiness = (r) => {
  const values = Object.values(r);
  if (values.some((item) => item.status === 'red')) return 'red';
  if (values.some((item) => item.status === 'yellow')) return 'yellow';
  return 'green';
};

export const getTaskDisplayStatus = (task) =>
  TASK_DISPLAY_STATUS_BY_STATUS[task?.status] ?? TASK_DISPLAY_STATUS.Pending;

export const isTaskReadinessComplete = (task) => {
  if (getTaskDisplayStatus(task) === TASK_DISPLAY_STATUS.Done) return true;
  const callbackStatus = `${task?.callbackStatus || ''}`.trim().toLowerCase();
  return callbackStatus === TASK_CALLBACK_STATUS.Success;
};

export const isTaskCanceled = (task) => Number(task?.status) === TASK_STATUS.Canceled;

export const isTaskUnableToComplete = (task) =>
  Number(task?.status) === TASK_STATUS.UnableToComplete;

export const isTaskCanceledForReadiness = (task) => isTaskCanceled(task);

export const getReadinessTaskStatusLabel = (task) => {
  if (isTaskCanceledForReadiness(task)) return 'Canceled';
  return getTaskDisplayStatus(task);
};

export const getPostVisitTaskDaysRolling = (task) => {
  const days = daysUntil(task?.due);
  return Number.isFinite(days) ? Math.max(0, -days) : 0;
};

export const getPostVisitCardStatus = (tasks = []) => {
  if (tasks.length === 0) return 'neutral';
  const completedCount = tasks.filter((task) => isTaskReadinessComplete(task)).length;
  if (completedCount === tasks.length) return 'green';
  const openTasks = tasks.filter((task) => !isTaskReadinessComplete(task));
  const oldestDaysRolling = Math.max(...openTasks.map(getPostVisitTaskDaysRolling));
  if (completedCount === 0 || oldestDaysRolling >= POST_VISIT_STALL_DAYS) return 'red';
  return 'yellow';
};

export const getTaskDisplayStatusLabel = (task) => {
  if (isTaskUnableToComplete(task)) return 'Unable To Complete';
  return getTaskDisplayStatus(task);
};

export const getDelegatedSubtaskMeta = (subtasks = []) => {
  const safeSubtasks = Array.isArray(subtasks) ? subtasks : [];
  const openSubtasks = safeSubtasks.filter(
    (task) => getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
  );
  const canceledCount = safeSubtasks.filter((task) => isTaskCanceled(task)).length;
  const doneCount = safeSubtasks.length - openSubtasks.length;
  const completedCount = Math.max(doneCount - canceledCount, 0);

  return {
    totalCount: safeSubtasks.length,
    openCount: openSubtasks.length,
    doneCount,
    completedCount,
    canceledCount,
    hasSubtasks: safeSubtasks.length > 0,
    waitingOnSubtasks: safeSubtasks.length > 0 && openSubtasks.length > 0,
    allDone: safeSubtasks.length > 0 && openSubtasks.length === 0,
    openSubtasks,
  };
};

export const isTaskVisible = (task) => task?.isVisible ?? true;

export const isLinkedTask = (task) => task?.isVisible === false;

export const isAutomaticWorkflowTask = (task) =>
  `${task?.stepType || ''}`.trim().toLowerCase() === TASK_STEP_TYPE.Automatic;

export const canMarkTaskComplete = (task) =>
  (task?.isVisible ?? true) && !isAutomaticWorkflowTask(task);

// A manual orchestration task only has a live task token once the workflow
// engine actually enters its state -- before that it's showing as a visible
// roadmap placeholder with nothing behind it to resolve. Completing it then
// would flip the DB row to Completed while the workflow itself stays stuck
// waiting on a token that was never issued. callbackStatus is set to
// "pending" the moment a real token exists, so its absence on an
// orchestration-managed manual task (one with a stateName) means "not real
// yet" -- distinct from isVisible, which this task can have flipped on early
// specifically so it shows up on the roadmap.
export const isTaskAwaitingRealCallback = (task) => {
  if (!task?.stateName) return false;
  if (`${task?.stepType || ''}`.trim().toLowerCase() !== TASK_STEP_TYPE.Manual) return false;
  const callbackStatus = `${task?.callbackStatus || ''}`.trim().toLowerCase();
  return (
    callbackStatus !== TASK_CALLBACK_STATUS.Pending &&
    callbackStatus !== TASK_CALLBACK_STATUS.Success
  );
};

export const shouldHideTaskByCallbackStatus = (task) => {
  if (isTaskVisible(task) && isAutomaticWorkflowTask(task)) return false;

  if (getTaskDisplayStatus(task) === TASK_DISPLAY_STATUS.Done) return true;

  const normalized = `${task?.callbackStatus || ''}`.trim().toLowerCase();
  return normalized === TASK_CALLBACK_STATUS.Success;
};

export const isTaskBlockedStatus = (task) => task?.blockedStatus === BLOCKED_STATUS.Blocked;

export const isTaskEscalatedStatus = (task) =>
  task?.escalatedStatus != null && task.escalatedStatus === ESCALATED_STATUS.Escalated;

export const isMessageTask = (task) =>
  task?.typeId === TaskType.MemberPortal.value &&
  task?.reasonId === TaskReason.NewMessageReceived.value;

const hasCareOrchestrationMetaEntry = (task) => !!`${task?.team || ''}`.trim();

export const isAssessmentTask = (task) =>
  task?.typeId === TaskType.Cognitive.value && hasCareOrchestrationMetaEntry(task);

export const getAssessmentTaskTypeByValue = (value) =>
  Object.values(ASSESSMENT_TASK_TYPES).find((assessmentType) => assessmentType.value === value);

export const getTaskAssessmentType = (task) => `${task?.assessmentType ?? ''}`.trim();

export const getTaskAssessmentTypeLabel = (task) => {
  const assessmentType = getTaskAssessmentType(task);
  if (!assessmentType) return '';

  return getAssessmentTaskTypeByValue(assessmentType)?.label ?? assessmentType;
};

export const getAssessmentTaskVariantByValue = (value) =>
  Object.values(ASSESSMENT_TASK_VARIANTS).find(
    (assessmentVariant) => assessmentVariant.value === value,
  );

export const getTaskAssessmentVariant = (task) => `${task?.assessmentVariant ?? ''}`.trim();

export const getTaskAssessmentVariantLabel = (task) => {
  const assessmentVariant = getTaskAssessmentVariant(task);
  if (!assessmentVariant) return '';

  return getAssessmentTaskVariantByValue(assessmentVariant)?.label ?? assessmentVariant;
};

export const isTaskCaregiverAssisted = (task) => !!task?.caregiverAssisted;

export const getClientDisplayName = (client) => {
  if (!client) return '';
  if (typeof client.getName === 'function') return client.getName(false, true);

  return `${client.firstName || ''} ${client.lastName || ''}`.trim();
};

export const getNameInitials = (name) => {
  const parts = `${name || ''}`.trim().split(/\s+/).filter(Boolean);

  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

export const normalizeTeamValues = (teamValues) => {
  const values = Array.isArray(teamValues) ? teamValues : [teamValues];

  return values.filter(Number.isInteger);
};

export const getTaskDueDateInputValue = (task) => {
  if (task?.due) return String(task.due).split('T')[0];

  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = `${currentDate.getMonth() + 1}`.padStart(2, '0');
  const day = `${currentDate.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const getTaskPriorityOrDefault = (
  task,
  allowedPriorities = [],
  fallbackPriority = TASK_PRIORITY.High,
) => {
  const taskPriority = `${task?.priority || ''}`.trim().toLowerCase();

  if (allowedPriorities.includes(taskPriority)) return taskPriority;

  return fallbackPriority;
};

export const getTaskDisplayTitle = (task, fallback = '') => {
  const assessmentTypeLabel = getTaskAssessmentTypeLabel(task);
  if (assessmentTypeLabel) return `Please review the ${assessmentTypeLabel} screening`;

  return task?.title ?? task?.subject ?? fallback;
};

export const getCareOrchestrationPath = (view, taskRoute = null) => {
  if (!view) return CARE_ORCHESTRATION_BASE_PATH;

  if (view === TAB_KEYS.TASKS) {
    const segments = [CARE_ORCHESTRATION_BASE_PATH, view];

    if (taskRoute?.statusFilter) {
      segments.push(taskRoute.statusFilter);
    }

    if (taskRoute?.patientId && taskRoute?.taskId) {
      segments.push(taskRoute.patientId, taskRoute.taskId);
    }

    return taskRoute?.statusFilter || (taskRoute?.patientId && taskRoute?.taskId)
      ? segments.join('/')
      : `${CARE_ORCHESTRATION_BASE_PATH}/${view}`;
  }

  return `${CARE_ORCHESTRATION_BASE_PATH}/${view}`;
};

export const parseCareOrchestrationRoute = (pathname) => {
  if (!pathname.startsWith(CARE_ORCHESTRATION_BASE_PATH)) {
    return { view: null, statusFilter: null, patientId: null, taskId: null };
  }

  const pathSuffix = pathname.slice(CARE_ORCHESTRATION_BASE_PATH.length).replace(/^\/+/, '');
  const [view = null, firstSegment = null, secondSegment = null, thirdSegment = null] =
    pathSuffix.split('/');

  if (view === TAB_KEYS.TASKS) {
    const statusFilter = TASK_STATUS_FILTER_KEYS.includes(firstSegment) ? firstSegment : null;

    return {
      view,
      statusFilter,
      patientId: statusFilter ? secondSegment : firstSegment,
      taskId: statusFilter ? thirdSegment : secondSegment,
    };
  }

  return {
    view,
    statusFilter: null,
    patientId: null,
    taskId: null,
  };
};

export const getTaskPatientId = (task) =>
  task?.patientId ??
  task?.patient_id ??
  task?.patient?.id ??
  task?.patient?.patientId ??
  task?.patient?.patient_id ??
  null;

export const getAppointmentPatientId = (appointment) =>
  appointment?.patientId ??
  appointment?.patient_id ??
  appointment?.patient?.id ??
  appointment?.patient?.patientId ??
  appointment?.patient?.patient_id ??
  null;

export const getTaskPatientGroupKey = (task) => {
  const patientId = getTaskPatientId(task);
  return patientId != null ? `patient-${patientId}` : `task-${task?.id}`;
};

export const formatPatientMrn = (mrn) => {
  if (!mrn) return null;
  return String(mrn).startsWith('MRN-') ? mrn : `MRN-${mrn}`;
};

export const getTaskDeepLinkUrl = (task, origin) => {
  const patientId = getTaskPatientId(task);
  const resolvedOrigin = origin ?? (typeof window !== 'undefined' ? window.location.origin : null);
  if (!task?.id || patientId == null || !resolvedOrigin) return null;

  return `${resolvedOrigin}${getCareOrchestrationPath(TAB_KEYS.TASKS, {
    patientId: `${patientId}`,
    taskId: `${task.id}`,
  })}`;
};

export const copyTextToClipboard = async (text) => {
  if (!text || typeof document === 'undefined') return false;

  if (navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      // Fall back to document.execCommand below for insecure dev origins.
    }
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.opacity = '0';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  textArea.setSelectionRange(0, text.length);

  let didCopy = false;

  try {
    didCopy = document.execCommand('copy');
  } finally {
    document.body.removeChild(textArea);
  }

  return didCopy;
};

export const formatRelativeTime = (value, now = new Date()) => {
  if (!value) return null;

  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return null;

  const diffMs = now.getTime() - then.getTime();
  if (diffMs < 0) return 'just now';

  const diffMinutes = Math.round(diffMs / 60000);
  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.round(diffHours / 24);
  return `${diffDays}d ago`;
};

export const buildTeamTaskShareText = ({
  patientName,
  visitLabel,
  visitDate,
  platformMrn,
  athenaId,
  teamLabel,
  openTasks = [],
  completedTasks = [],
  visitUrl,
}) => {
  const lines = [[patientName, visitLabel, visitDate].filter(Boolean).join(' · ')];

  const ids = [
    platformMrn ? `Platform MRN: ${platformMrn}` : null,
    athenaId ? `Athena ID: ${athenaId}` : null,
  ].filter(Boolean);
  if (ids.length > 0) lines.push(ids.join(' · '));

  const rollup =
    [
      openTasks.length > 0 ? `${openTasks.length} open` : null,
      completedTasks.length > 0 ? `${completedTasks.length} done` : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'no tasks';
  lines.push(`${teamLabel} — ${rollup}`);

  if (openTasks.length > 0) {
    lines.push('');
    openTasks.forEach((task) => {
      lines.push(
        `• ${task.title} — ${task.dueText || '-'} — ${`${task.priority || ''}`.toUpperCase()} — ${
          task.owner || 'Unassigned'
        } — ${task.url || ''}`,
      );
      if (task.blockedByTitle) lines.push(`  ↳ blocked by: ${task.blockedByTitle}`);
    });
  }

  if (completedTasks.length > 0) {
    lines.push('');
    completedTasks.forEach((task) =>
      lines.push(`✓ ${task.title} (done${task.completedBy ? ` — ${task.completedBy}` : ''})`),
    );
  }

  lines.push('', `Visit: ${visitUrl || ''}`);

  return lines.join('\n');
};

export const matchesTaskStatusFilter = (task, statusFilter) => {
  if (!task) return false;

  switch (statusFilter) {
    case 'open':
      return getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done;
    case 'critical':
      return (
        task.priority === TASK_PRIORITY.Critical &&
        getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done
      );
    case 'blocked':
      return isTaskBlockedStatus(task) && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done;
    case 'escalated':
      return isTaskEscalatedStatus(task) && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done;
    case 'messages':
      return isMessageTask(task) && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done;
    case 'assessments':
      return isAssessmentTask(task) && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done;
    default:
      return true;
  }
};

const isSameWorkflowScope = (task, candidateTask, fallbackAppointmentId = null) => {
  const workflowInstanceId =
    `${task?.workflowInstanceId || task?.workflow_instance_id || ''}`.trim();
  const candidateWorkflowInstanceId =
    `${candidateTask?.workflowInstanceId || candidateTask?.workflow_instance_id || ''}`.trim();

  if (workflowInstanceId || candidateWorkflowInstanceId) {
    return !!workflowInstanceId && workflowInstanceId === candidateWorkflowInstanceId;
  }

  const appointmentId = `${task?.externalObjectId || fallbackAppointmentId || ''}`.trim();
  const candidateAppointmentId =
    `${candidateTask?.externalObjectId || fallbackAppointmentId || ''}`.trim();

  return !!appointmentId && appointmentId === candidateAppointmentId;
};

export const getFirstVisibleIncompleteSiblingTask = (
  task,
  tasks = [],
  fallbackAppointmentId = null,
) => {
  if (!task) return null;

  const appointmentId = `${task?.externalObjectId || fallbackAppointmentId || ''}`.trim();
  if (!appointmentId) return null;

  return (
    tasks.find((candidateTask) => {
      if (!candidateTask || candidateTask?.id === task?.id) return false;

      const candidateAppointmentId =
        `${candidateTask?.externalObjectId || fallbackAppointmentId || ''}`.trim();
      if (candidateAppointmentId !== appointmentId) return false;
      if (!isSameWorkflowScope(task, candidateTask, fallbackAppointmentId)) return false;

      return isTaskVisible(candidateTask) && !isTaskReadinessComplete(candidateTask);
    }) ?? null
  );
};

export const getTaskStateName = (task) => `${task?.stateName || task?.state_name || ''}`.trim();

export const getTaskDependency = (task) => {
  const dependency = task?.dependency;
  if (dependency && typeof dependency === 'object' && !Array.isArray(dependency)) {
    return dependency;
  }

  const legacyStateNames = task?.dependencyStateNames ?? task?.dependency_state_names;
  if (!Array.isArray(legacyStateNames)) return null;

  const stateNames = legacyStateNames
    .map((stateName) => `${stateName || ''}`.trim())
    .filter(Boolean);
  if (!stateNames.length) return null;

  return {
    kind: stateNames.length > 1 ? 'parallel' : 'linked',
    stateNames,
  };
};

export const getTaskDecisionOptions = (task) => {
  const dependency = getTaskDependency(task);
  if (dependency?.type !== 'decision') return [];

  const answers = Array.isArray(dependency?.answers) ? dependency.answers : [];
  const normalizedAnswers = answers
    .map((answer) => ({
      value: `${answer?.value || ''}`.trim(),
      label: `${answer?.label || answer?.value || ''}`.trim(),
    }))
    .filter((answer) => answer.value && answer.label);

  if (normalizedAnswers.length) return normalizedAnswers;

  const signature = `${dependency?.signature || ''}`.trim().toLowerCase();
  if (signature === 'yes_no') {
    return [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ];
  }

  if (signature === 'trinary') {
    return [
      { value: 'completed', label: 'Completed' },
      { value: 'will-complete', label: 'Will Complete' },
      { value: 'declined', label: 'Declined' },
    ];
  }

  return [];
};

export const hasTaskDecision = (task) => getTaskDecisionOptions(task).length > 0;

export const getTaskCancelDecisionPayload = (task) => {
  const dependency = getTaskDependency(task);
  if (dependency?.type !== 'decision') return null;

  const answers = Array.isArray(dependency?.answers) ? dependency.answers : [];
  const nextAnswer = answers.find((answer) => {
    if (!answer || typeof answer !== 'object') return false;

    const action = `${answer.action || ''}`.trim().toLowerCase().replace(/-/g, '_');
    const nextState = `${answer.next_state || answer.nextState || ''}`.trim();
    return action === 'spawn' || !!nextState;
  });

  const value = `${nextAnswer?.value || ''}`.trim();
  if (!value) return null;

  return {
    result: value,
    decision: value,
  };
};

export const getTaskDependencyStateNames = (task) => {
  const dependency = getTaskDependency(task);
  const dependencyStateNames = dependency?.stateNames;
  if (!Array.isArray(dependencyStateNames)) return [];

  return dependencyStateNames.map((stateName) => `${stateName || ''}`.trim()).filter(Boolean);
};

export const isWorkflowBlockedTask = (task) =>
  isLinkedTask(task) && getTaskDependency(task)?.kind === 'parallel';

export const isWorkflowLinkedChildTask = (task) => {
  if (!isLinkedTask(task)) return false;

  const dependency = getTaskDependency(task);
  return !dependency || dependency.kind === 'linked';
};

export const getIncompleteDependencyTasks = (task, tasks = [], fallbackAppointmentId = null) => {
  const dependencyStateNames = getTaskDependencyStateNames(task);
  if (!dependencyStateNames.length) return [];

  const dependencyStateNameSet = new Set(dependencyStateNames);

  return tasks.filter((candidateTask) => {
    if (!candidateTask || candidateTask?.id === task?.id) return false;
    if (!isSameWorkflowScope(task, candidateTask, fallbackAppointmentId)) return false;
    if (!dependencyStateNameSet.has(getTaskStateName(candidateTask))) return false;

    return !isTaskReadinessComplete(candidateTask);
  });
};

export const getLinkedTaskParentTask = (task, tasks = [], fallbackAppointmentId = null) => {
  const dependency = getTaskDependency(task);
  if (dependency?.kind === 'parallel') return null;

  const dependencyTasks = getIncompleteDependencyTasks(task, tasks, fallbackAppointmentId);
  if (dependencyTasks.length) return dependencyTasks[0];

  if (dependency) return null;

  return getFirstVisibleIncompleteSiblingTask(task, tasks, fallbackAppointmentId);
};

export const getLinkedTaskWaitingForTitle = (task, tasks = [], fallbackAppointmentId = null) => {
  const dependency = getTaskDependency(task);
  const dependencyTasks = getIncompleteDependencyTasks(task, tasks, fallbackAppointmentId);

  if (dependencyTasks.length) {
    const [firstTask, ...additionalTasks] = dependencyTasks;
    const firstTitle = getTaskDisplayTitle(firstTask);
    if (!additionalTasks.length) return firstTitle;

    if (dependency?.parentStateName) {
      return `${dependency.parentStateName.replace(/_/g, ' ')}: ${firstTitle} + ${
        additionalTasks.length
      } more`;
    }

    return `${firstTitle} + ${additionalTasks.length} more`;
  }

  if (dependency) return null;

  const fallbackTask = getFirstVisibleIncompleteSiblingTask(task, tasks, fallbackAppointmentId);
  return fallbackTask ? getTaskDisplayTitle(fallbackTask) : null;
};

export const getAppointmentReadinessStatus = (tasks = []) => {
  if (
    tasks.some(
      (task) => !isTaskReadinessComplete(task) && task?.priority === TASK_PRIORITY.Critical,
    )
  ) {
    return 'red';
  }
  if (tasks.some((task) => !isTaskReadinessComplete(task))) return 'yellow';
  if (tasks.some((task) => isTaskCanceledForReadiness(task))) return 'orange';
  if (tasks.length > 0) return 'green';
  return 'default';
};

export const dayName = (d) =>
  ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(d).getDay()];

export const monthDay = (d) => {
  const dt = new Date(d);
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][dt.getMonth()]} ${dt.getDate()}`;
};

export const sortAppointmentsByDate = (appointments = []) =>
  [...appointments].sort((a, b) => new Date(a.date) - new Date(b.date));

export const groupAppointmentsByDate = (appointments) => {
  const grouped = {};
  appointments.forEach((appt) => {
    (grouped[appt.date] = grouped[appt.date] || []).push(appt);
  });
  return grouped;
};

const normalizeProviderAthenaId = (providerAthenaId) => {
  if (providerAthenaId == null || providerAthenaId === '') return null;
  return `${providerAthenaId}`.trim();
};

export const getClientByAthenaProviderId = (clients = [], providerAthenaId) => {
  const normalizedProviderAthenaId = normalizeProviderAthenaId(providerAthenaId);
  if (!normalizedProviderAthenaId) return null;

  return (
    clients.find(
      (client) =>
        normalizeProviderAthenaId(client?.athenaProviderId) === normalizedProviderAthenaId,
    ) ?? null
  );
};

export const formatClinicianName = (client) => {
  const firstName = `${client?.firstName || ''}`.trim();
  const lastName = `${client?.lastName || ''}`.trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const credentials = `${client?.credentials || ''}`.trim();

  if (!fullName) return '-';
  return credentials ? `${fullName}, ${credentials}` : fullName;
};

export const buildEmptyEscalationTeamGroups = () =>
  ESCALATED_TO_OPTIONS.reduce((acc, option) => {
    acc[option.value] = [];
    return acc;
  }, {});

export const groupClientsByEscalationTeam = (clients = []) => {
  const grouped = buildEmptyEscalationTeamGroups();

  clients.forEach((client) => {
    const teamValues = normalizeTeamValues(ROLE_ID_TO_ESCALATION_TEAM[client?.roleId]);
    if (!teamValues.length) return;

    const label = getClientDisplayName(client);
    if (!label) return;

    const clientOption = {
      id: client.id,
      client,
      label,
      initials: getNameInitials(label),
      role: client?.role || (client?.roleId ? getRoleById(client.roleId) : ''),
    };

    teamValues.forEach((teamValue) => {
      grouped[teamValue].push(clientOption);
    });
  });

  Object.keys(grouped).forEach((teamValue) => {
    grouped[teamValue].sort((a, b) => a.label.localeCompare(b.label));
  });

  return grouped;
};

export const getPatientDisplayNameScore = (patient = {}) => {
  let score = 0;
  if (`${patient?.name || ''}`.trim()) score += 4;
  if (`${patient?.preferredName || ''}`.trim()) score += 3;
  if (`${patient?.firstName || ''}`.trim()) score += 2;
  if (`${patient?.lastName || ''}`.trim()) score += 1;
  return score;
};

export const mergePatientRecords = (currentPatient = {}, nextPatient = {}) => {
  const currentScore = getPatientDisplayNameScore(currentPatient);
  const nextScore = getPatientDisplayNameScore(nextPatient);

  if (nextScore >= currentScore) {
    return {
      ...currentPatient,
      ...nextPatient,
    };
  }

  return {
    ...nextPatient,
    ...currentPatient,
  };
};

export const getTaskSearchPlaceholder = (taskSortMode) =>
  TASK_QUEUE_SEARCH_PLACEHOLDERS[taskSortMode] ??
  TASK_QUEUE_SEARCH_PLACEHOLDERS[TASK_QUEUE_SORT_MODE.PatientPriority];

export const getPatientSearchPathway = (patient) => {
  if (patient?.pathway) return patient.pathway;

  const hasActiveCognitive = patient?.cognitiveStatus === PlatformStatus.Active;
  const hasActiveDiabetes = patient?.diabetesStatus === PlatformStatus.Active;

  if (hasActiveCognitive) return PATHWAY.COGNITIVE;
  if (hasActiveDiabetes) return PATHWAY.DIABETES;
  return null;
};

export const isPriorityTask = (task) => {
  if (!task?.due) return false;
  return daysUntil(task.due) < 7;
};

export const getDueTimestamp = (task) =>
  task?.due ? new Date(task.due).getTime() : Number.POSITIVE_INFINITY;

export const getGroupPathway = (tasks = [], fallbackPathway = null) => {
  const pathwayCounts = tasks.reduce((acc, task) => {
    const pathway = task?.patient?.pathway;
    if (!pathway) return acc;
    acc[pathway] = (acc[pathway] ?? 0) + 1;
    return acc;
  }, {});

  const topPathway = Object.entries(pathwayCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
  return topPathway ?? fallbackPathway ?? null;
};

export const getTaskClinician = (task, appointmentsById = {}, clients = []) => {
  const appointmentId = `${task?.externalObjectId || ''}`.trim();
  if (!appointmentId) return null;

  const linkedAppointment = appointmentsById[appointmentId];
  const clinicianClient = getClientByAthenaProviderId(clients, linkedAppointment?.providerAthenaId);
  const clinician = formatClinicianName(clinicianClient);
  return clinician === '-' ? null : clinician;
};

export const getGroupClinician = (
  tasks = [],
  appointmentsById = {},
  clients = [],
  fallbackClinician = null,
) => {
  const clinician = tasks
    .map((task) => getTaskClinician(task, appointmentsById, clients))
    .find(Boolean);
  return clinician ?? fallbackClinician ?? null;
};

export const getTaskPhase = (task) => {
  const taskPhase = task?.phase;
  if (taskPhase != null && `${taskPhase}`.trim() !== '') return taskPhase;
  return null;
};

export const getGroupPhase = (tasks = [], fallbackPhase = null) => {
  const phase = tasks.map(getTaskPhase).find((value) => value != null && `${value}`.trim() !== '');
  return phase ?? fallbackPhase ?? null;
};

export const buildLinkedTaskStructure = (tasks = [], fallbackAppointmentId = null) => {
  const childIds = new Set();
  const linkedChildIds = new Set();
  const subtaskChildIds = new Set();
  const childTasksByParentId = new Map();
  const linkedChildTasksByParentId = new Map();
  const subtaskChildTasksByParentId = new Map();
  const parentIdByChildId = new Map();

  const ensureParentBucket = (collection, parentId) => {
    if (!collection.has(parentId)) {
      collection.set(parentId, []);
    }

    return collection.get(parentId);
  };

  tasks.forEach((task) => {
    const taskId = `${task?.id || ''}`;
    if (!taskId) return;

    const parentTaskId = `${task?.parentTaskId || ''}`.trim();
    if (parentTaskId) {
      childIds.add(taskId);
      subtaskChildIds.add(taskId);
      parentIdByChildId.set(taskId, parentTaskId);
      ensureParentBucket(childTasksByParentId, parentTaskId).push(task);
      ensureParentBucket(subtaskChildTasksByParentId, parentTaskId).push(task);
      return;
    }

    if (!isWorkflowLinkedChildTask(task)) return;

    const parentTask = getLinkedTaskParentTask(task, tasks, fallbackAppointmentId);
    if (!parentTask?.id) return;

    const parentId = `${parentTask.id}`;
    childIds.add(taskId);
    linkedChildIds.add(taskId);
    parentIdByChildId.set(taskId, parentId);
    ensureParentBucket(childTasksByParentId, parentId).push(task);
    ensureParentBucket(linkedChildTasksByParentId, parentId).push(task);
  });

  return {
    childIds,
    linkedChildIds,
    subtaskChildIds,
    childTasksByParentId,
    linkedChildTasksByParentId,
    subtaskChildTasksByParentId,
    parentIdByChildId,
    parentChildCount: new Map(
      Array.from(childTasksByParentId.entries()).map(([parentId, childTasks]) => [
        parentId,
        childTasks.length,
      ]),
    ),
    parentLinkedChildCount: new Map(
      Array.from(linkedChildTasksByParentId.entries()).map(([parentId, childTasks]) => [
        parentId,
        childTasks.length,
      ]),
    ),
    parentSubtaskChildCount: new Map(
      Array.from(subtaskChildTasksByParentId.entries()).map(([parentId, childTasks]) => [
        parentId,
        childTasks.length,
      ]),
    ),
  };
};

export const buildRenderableSectionTasks = (
  sectionTasks = [],
  linkedTaskStructure,
  expandedLinkedTasks = {},
) => {
  const { childIds, linkedChildTasksByParentId, subtaskChildTasksByParentId, parentIdByChildId } =
    linkedTaskStructure;
  const sectionTaskIds = new Set(sectionTasks.map((t) => `${t?.id || ''}`).filter(Boolean));
  const renderableTasks = [];
  const addedTaskIds = new Set();

  const collectDescendantTasks = (parentTaskId) => {
    const childTasks = [
      ...(linkedChildTasksByParentId.get(parentTaskId) || []),
      ...(subtaskChildTasksByParentId.get(parentTaskId) || []),
    ];

    childTasks.forEach((childTask) => {
      const childTaskId = `${childTask?.id || ''}`;
      if (!childTaskId || addedTaskIds.has(childTaskId)) return;
      renderableTasks.push(childTask);
      addedTaskIds.add(childTaskId);

      if (
        getTaskDisplayStatus(childTask) !== TASK_DISPLAY_STATUS.Done &&
        expandedLinkedTasks[childTaskId]
      ) {
        collectDescendantTasks(childTaskId);
      }
    });
  };

  sectionTasks.forEach((task) => {
    const taskId = `${task?.id || ''}`;
    if (!taskId || addedTaskIds.has(taskId)) return;
    if (childIds.has(taskId) && sectionTaskIds.has(parentIdByChildId?.get(taskId))) return;

    renderableTasks.push(task);
    addedTaskIds.add(taskId);

    if (getTaskDisplayStatus(task) === TASK_DISPLAY_STATUS.Done) return;
    if (!expandedLinkedTasks[taskId]) return;

    collectDescendantTasks(taskId);
  });

  return renderableTasks;
};

export const buildRenderableSectionItems = (
  sectionTasks = [],
  linkedTaskStructure,
  expandedLinkedTasks = {},
  delegatedSubtasksByParentId = {},
) => {
  const { childIds, linkedChildTasksByParentId, subtaskChildTasksByParentId, parentIdByChildId } =
    linkedTaskStructure;
  const sectionTaskIds = new Set(sectionTasks.map((t) => `${t?.id || ''}`).filter(Boolean));
  const renderableItems = [];
  const addedTaskIds = new Set();

  const buildChildItemsFor = (parentTask) => {
    const parentTaskId = `${parentTask?.id || ''}`;
    const linkedChildren = linkedChildTasksByParentId.get(parentTaskId) || [];
    const localSubtaskChildren = subtaskChildTasksByParentId.get(parentTaskId) || [];
    const hydratedSubtaskChildren = delegatedSubtasksByParentId[parentTaskId] || [];
    const isParentDone = getTaskDisplayStatus(parentTask) === TASK_DISPLAY_STATUS.Done;
    const subtaskChildren = Array.from(
      [...hydratedSubtaskChildren, ...localSubtaskChildren]
        .reduce((acc, sub) => {
          const subId = `${sub?.id || ''}`.trim();
          if (subId) acc.set(subId, sub);
          return acc;
        }, new Map())
        .values(),
    ).filter((subtask) => !isTaskCanceled(subtask) && !isParentDone);
    const hasLinkedChildren = linkedChildren.length > 0;
    const hasSubtaskChildren = subtaskChildren.length > 0;
    const showLabels = hasLinkedChildren && hasSubtaskChildren;

    if (!hasLinkedChildren && !hasSubtaskChildren) return [];

    const childItems = [];

    if (hasLinkedChildren) {
      if (showLabels) {
        childItems.push({
          type: 'label',
          key: `${parentTaskId}-linked-label`,
          label: 'Linked',
        });
      }

      linkedChildren.forEach((childTask) => {
        const childTaskId = `${childTask?.id || ''}`;
        if (!childTaskId || addedTaskIds.has(childTaskId)) return;
        childItems.push({ type: 'task', task: childTask });
        addedTaskIds.add(childTaskId);

        if (expandedLinkedTasks[childTaskId]) {
          const nestedChildItems = buildChildItemsFor(childTask);
          if (nestedChildItems.length) {
            childItems.push({
              type: 'child-group',
              key: `${childTaskId}-child-group`,
              items: nestedChildItems,
            });
          }
        }
      });
    }

    if (hasSubtaskChildren) {
      if (showLabels) {
        childItems.push({
          type: 'label',
          key: `${parentTaskId}-subtasks-label`,
          label: 'Sub-tasks',
        });
      }

      subtaskChildren.forEach((childTask) => {
        const childTaskId = `${childTask?.id || ''}`;
        if (!childTaskId || addedTaskIds.has(childTaskId)) return;
        childItems.push({ type: 'task', task: childTask });
        addedTaskIds.add(childTaskId);
      });
    }

    return childItems;
  };

  sectionTasks.forEach((task) => {
    const taskId = `${task?.id || ''}`;
    if (!taskId || addedTaskIds.has(taskId)) return;
    if (childIds.has(taskId) && sectionTaskIds.has(parentIdByChildId?.get(taskId))) return;

    renderableItems.push({ type: 'task', task });
    addedTaskIds.add(taskId);

    if (!expandedLinkedTasks[taskId]) return;

    const childItems = buildChildItemsFor(task);

    if (childItems.length) {
      renderableItems.push({
        type: 'child-group',
        key: `${taskId}-child-group`,
        items: childItems,
      });
    }
  });

  return renderableItems;
};

export const enhanceTaskQueueGroup = (group) => ({
  ...group,
  linkedTaskStructure: buildLinkedTaskStructure(group.tasks),
  criticalCount: group.tasks.filter((task) => task.priority === TASK_PRIORITY.Critical).length,
  blockedCount: group.tasks.filter((task) => isTaskBlockedStatus(task)).length,
  escalatedCount: group.tasks.filter((task) => isTaskEscalatedStatus(task)).length,
});

export const buildPatientSearchMetadataById = ({
  tasks = [],
  appointmentsById = {},
  clients = [],
} = {}) => {
  const groupedPatients = tasks.reduce((acc, task) => {
    const patientId = `${task?.patientId ?? task?.patient?.id ?? ''}`.trim();
    if (!patientId) return acc;

    if (!acc[patientId]) {
      acc[patientId] = {
        patient: task?.patient ?? {},
        tasks: [],
      };
    } else {
      acc[patientId].patient = mergePatientRecords(acc[patientId].patient, task?.patient ?? {});
    }

    acc[patientId].tasks.push(task);
    return acc;
  }, {});

  return Object.entries(groupedPatients).reduce((acc, [patientId, group]) => {
    const fallbackPhase = group.patient?.phase ?? 1;
    acc[patientId] = {
      pathway: getGroupPathway(group.tasks, group.patient?.pathway ?? null),
      phase: getGroupPhase(group.tasks, fallbackPhase),
      clinician: getGroupClinician(
        group.tasks,
        appointmentsById,
        clients,
        group.patient?.clinician ?? null,
      ),
      patientMrn: formatPatientMrn(group.patient?.mrn) || (patientId ? `MRN-${patientId}` : null),
    };
    return acc;
  }, {});
};

export const getPatientSearchLabel = (patient, patientSearchMetadataById = {}) => {
  const fallbackName = `${patient?.name || ''}`.trim();
  const firstName = patient?.preferredName || patient?.firstName || '';
  const lastName = patient?.lastName || '';
  const name = `${firstName} ${lastName}`.trim() || fallbackName;
  const metadata = patientSearchMetadataById[`${patient?.id ?? ''}`] ?? {};
  const mrn =
    metadata.patientMrn || formatPatientMrn(patient?.mrn) || formatPatientMrn(patient?.id);

  return `${name} ${mrn || ''}`.trim();
};

export const buildTaskSearchOptions = ({
  tasks = [],
  taskSortMode,
  patientSearchMetadataById = {},
} = {}) => {
  if (taskSortMode === TASK_QUEUE_SORT_MODE.ReasonPriority) {
    const countsByReasonId = tasks.reduce((acc, task) => {
      const reasonId = Number(task?.reasonId);
      if (!Number.isInteger(reasonId)) return acc;
      acc[reasonId] = (acc[reasonId] ?? 0) + 1;
      return acc;
    }, {});

    return Array.from(
      tasks
        .reduce((acc, task) => {
          const reasonId = Number(task?.reasonId);
          if (!Number.isInteger(reasonId) || acc.has(reasonId)) return acc;

          acc.set(reasonId, {
            key: `reason-${reasonId}`,
            label: TASK_QUEUE_REASON_LABELS[reasonId] || 'Unknown Reason',
            value: {
              kind: 'reason',
              reasonId,
            },
            count: countsByReasonId[reasonId] ?? 0,
          });

          return acc;
        }, new Map())
        .values(),
    );
  }

  if (taskSortMode === TASK_QUEUE_SORT_MODE.Priority) {
    const countsByPriority = tasks.reduce((acc, task) => {
      const priority = `${task?.priority || ''}`;
      if (!priority) return acc;
      acc[priority] = (acc[priority] ?? 0) + 1;
      return acc;
    }, {});

    return TASK_QUEUE_PRIORITY_GROUPS.filter(
      (priorityGroup) => countsByPriority[priorityGroup.key],
    ).map((priorityGroup) => ({
      key: `priority-search-${priorityGroup.key}`,
      label: priorityGroup.label,
      value: {
        kind: 'priority',
        priority: priorityGroup.key,
      },
      count: countsByPriority[priorityGroup.key] ?? 0,
    }));
  }

  return Array.from(
    tasks
      .reduce((acc, task) => {
        const patientId = `${task?.patientId ?? task?.patient?.id ?? ''}`.trim();
        if (!patientId) return acc;

        const existingOption = acc.get(patientId);
        const patient = mergePatientRecords(existingOption?.value ?? {}, task?.patient ?? {});
        const resolvedPatientId = task?.patientId ?? task?.patient?.id ?? patientId;

        acc.set(patientId, {
          key: `patient-${patientId}`,
          label: getPatientSearchLabel(
            {
              ...patient,
              id: resolvedPatientId,
            },
            patientSearchMetadataById,
          ),
          value: {
            kind: 'patient',
            ...patient,
            id: resolvedPatientId,
          },
        });

        return acc;
      }, new Map())
      .values(),
  );
};

export const getTaskSearchSelectionLabel = (selection, patientSearchMetadataById = {}) => {
  if (!selection) return '';

  if (selection.kind === 'reason') {
    return TASK_QUEUE_REASON_LABELS[Number(selection.reasonId)] || 'Unknown Reason';
  }

  if (selection.kind === 'priority') {
    return (
      TASK_QUEUE_PRIORITY_GROUPS.find((group) => group.key === selection.priority)?.label ||
      'Priority'
    );
  }

  return getPatientSearchLabel(selection, patientSearchMetadataById);
};

export const buildPatientGroups = ({
  tasks = [],
  appointmentsById = {},
  clients = [],
  preserveOrder = false,
} = {}) => {
  const grouped = new Map();

  tasks.forEach((task) => {
    const key = getTaskPatientGroupKey(task);
    const patient = task?.patient ?? {};
    const group = grouped.get(key) ?? {
      key,
      kind: 'patient',
      patient,
      patientName: patient?.name || 'Unknown Patient',
      patientMrn:
        formatPatientMrn(patient?.mrn) ||
        (task?.patientId != null
          ? `MRN-${task.patientId}`
          : task?.patient?.id != null
            ? `MRN-${task.patient.id}`
            : null),
      patientPhase: getTaskPhase(task),
      patientClinician: getTaskClinician(task, appointmentsById, clients),
      pathway: patient?.pathway ?? null,
      tasks: [],
    };

    group.tasks.push(task);
    grouped.set(key, group);
  });

  const patientGroups = [...grouped.values()].map((group) => {
    const nextGroup = enhanceTaskQueueGroup(group);
    const priorityTasks = group.tasks.filter((task) => {
      if (
        nextGroup.linkedTaskStructure.childIds.has(`${task?.id || ''}`) &&
        getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done
      ) {
        return false;
      }
      return isPriorityTask(task);
    });
    const futureTasks = group.tasks.filter((task) => {
      if (
        nextGroup.linkedTaskStructure.childIds.has(`${task?.id || ''}`) &&
        getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done
      ) {
        return false;
      }
      return !isPriorityTask(task);
    });
    const nextPriorityDue = priorityTasks.reduce(
      (min, task) => Math.min(min, getDueTimestamp(task)),
      Number.POSITIVE_INFINITY,
    );
    const nextDue = group.tasks.reduce(
      (min, task) => Math.min(min, getDueTimestamp(task)),
      Number.POSITIVE_INFINITY,
    );
    const nextDueTask = group.tasks.find(
      (task) => getDueTimestamp(task) === nextDue && Number.isFinite(nextDue),
    );

    return {
      ...nextGroup,
      patientPhase: getGroupPhase(group.tasks, group.patientPhase),
      patientClinician: getGroupClinician(
        group.tasks,
        appointmentsById,
        clients,
        group.patientClinician,
      ),
      pathway: getGroupPathway(group.tasks, group.pathway),
      priorityTasks,
      futureTasks,
      nowCount: priorityTasks.length,
      laterCount: futureTasks.length,
      nextPriorityDue,
      nextDue,
      nextDueLabel: nextDueTask?.due ? formatDue(nextDueTask.due).text : '-',
    };
  });

  if (preserveOrder) {
    return patientGroups;
  }

  return patientGroups.sort((a, b) => {
    if (a.nextPriorityDue !== b.nextPriorityDue) return a.nextPriorityDue - b.nextPriorityDue;
    if (a.nextDue !== b.nextDue) return a.nextDue - b.nextDue;
    return a.patientName.localeCompare(b.patientName);
  });
};

export const buildReasonGroups = (tasks = []) => {
  const grouped = new Map();

  tasks.forEach((task) => {
    const reasonId = Number(task?.reasonId);
    const key = `reason-${Number.isInteger(reasonId) ? reasonId : 'unknown'}`;
    const group = grouped.get(key) ?? {
      key,
      kind: 'bucket',
      bucketType: 'reason',
      title: TASK_QUEUE_REASON_LABELS[reasonId] || 'Unknown Reason',
      badgeVariant: 'outline',
      tasks: [],
    };

    group.tasks.push(task);
    grouped.set(key, group);
  });

  return [...grouped.values()].map(enhanceTaskQueueGroup);
};

export const buildPriorityGroups = (tasks = []) =>
  TASK_QUEUE_PRIORITY_GROUPS.map((priorityGroup) =>
    enhanceTaskQueueGroup({
      key: `priority-${priorityGroup.key}`,
      kind: 'bucket',
      bucketType: 'priority',
      title: priorityGroup.label,
      badgeVariant: priorityGroup.badgeVariant,
      tasks: tasks.filter((task) => task?.priority === priorityGroup.key),
    }),
  ).filter((group) => group.tasks.length);

export const buildTaskQueuePrioritySummary = (tasks = []) =>
  tasks.reduce(
    (acc, task) => {
      const priority = task?.priority;
      if (priority && Object.prototype.hasOwnProperty.call(acc, priority)) {
        acc[priority] += 1;
      }
      return acc;
    },
    {
      [TASK_PRIORITY.Critical]: 0,
      [TASK_PRIORITY.High]: 0,
      [TASK_PRIORITY.Medium]: 0,
      [TASK_PRIORITY.Low]: 0,
    },
  );

export const buildTaskQueueSummaryLabel = (taskSortMode, groupCount, totalTaskCount) => {
  if (taskSortMode === TASK_QUEUE_SORT_MODE.ReasonPriority) {
    return `${groupCount} reasons · ${totalTaskCount} tasks`;
  }

  if (taskSortMode === TASK_QUEUE_SORT_MODE.Priority) {
    return `${groupCount} priority groups · ${totalTaskCount} tasks`;
  }

  return `${groupCount} members · ${totalTaskCount} tasks`;
};

export const normalizeEscalationPayload = (payload = {}) => {
  const normalizedEscalatedTo = payload.escalatedTo == null ? null : Number(payload.escalatedTo);
  const normalizedEscalatedToUser =
    payload.escalatedToUser == null ? null : Number(payload.escalatedToUser);

  return {
    escalatedReason: `${payload.escalatedReason || ''}`.trim(),
    escalatedTo: Number.isInteger(normalizedEscalatedTo) ? normalizedEscalatedTo : null,
    escalatedToUser: Number.isInteger(normalizedEscalatedToUser) ? normalizedEscalatedToUser : null,
  };
};

export const isEscalationPayloadValid = (payload = {}) => {
  const normalizedPayload = normalizeEscalationPayload(payload);
  const hasTeamTarget = Number.isInteger(normalizedPayload.escalatedTo);
  const hasUserTarget = Number.isInteger(normalizedPayload.escalatedToUser);
  return hasTeamTarget !== hasUserTarget;
};

export const buildEscalationPayload = ({
  escalatedReason = '',
  selectedTeam = null,
  selectedClientId = null,
  isSpecificPersonChecked = false,
} = {}) =>
  normalizeEscalationPayload({
    escalatedReason,
    escalatedTo: isSpecificPersonChecked ? null : selectedTeam,
    escalatedToUser: isSpecificPersonChecked ? selectedClientId : null,
  });

export const isTaskNotificationChannelEnabled = (preferences, channelKey) => {
  if (!preferences || !channelKey) return false;

  if (preferences?.channels?.[channelKey]?.enabled) return true;

  return Object.values(preferences?.categories || {}).some(
    (categoryPreferences) => !!categoryPreferences?.[channelKey],
  );
};

export const isBrowserNotificationsSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window;

export const getTaskBrowserNotificationPermission = () => {
  if (!isBrowserNotificationsSupported()) return 'denied';
  return window.Notification.permission;
};

export const requestTaskBrowserNotificationPermission = async () => {
  if (!isBrowserNotificationsSupported()) return 'denied';

  try {
    return await window.Notification.requestPermission();
  } catch {
    return 'denied';
  }
};

export const showTaskBrowserNotification = (notification, { onClick } = {}) => {
  if (getTaskBrowserNotificationPermission() !== 'granted') return null;

  const copy = NOTIFICATION_BROWSER_COPY_BY_CATEGORY[notification?.category];
  if (!copy) return null;

  const browserNotification = new window.Notification(copy.title, {
    body: copy.body,
    requireInteraction: copy.requireInteraction,
    tag: `${notification?.category || 'notification'}-${notification?.taskId || notification?.id || 'unknown'}`,
  });

  browserNotification.onclick = async () => {
    try {
      window.focus();
    } catch {
      // no-op
    }

    if (onClick) {
      await onClick(notification);
    }

    browserNotification.close();
  };

  return browserNotification;
};

let audioContext = null;

const getAudioContext = () => {
  if (typeof window === 'undefined') return null;

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;

  try {
    if (!audioContext) {
      audioContext = new AudioContextClass();
    }

    return audioContext;
  } catch {
    return null;
  }
};

export const unlockTaskNotificationSound = async () => {
  const context = getAudioContext();
  if (!context) return false;

  if (context.state === 'running') return true;

  try {
    await context.resume();
    return context.state === 'running';
  } catch {
    return false;
  }
};

const playTone = (context, frequency, startTime, duration) => {
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.value = frequency;

  gain.gain.setValueAtTime(0.15, startTime);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

  oscillator.connect(gain);
  gain.connect(context.destination);

  oscillator.start(startTime);
  oscillator.stop(startTime + duration);
};

export const playTaskNotificationSound = async (isCritical) => {
  const context = getAudioContext();
  if (!context) return;

  if (context.state === 'suspended') {
    try {
      await context.resume();
    } catch {
      return;
    }
  }

  const now = context.currentTime;

  if (isCritical) {
    playTone(context, 880, now, 0.15);
    playTone(context, 880, now + 0.2, 0.15);
    return;
  }

  playTone(context, 660, now, 0.2);
};
